/**
 * Pasture for Life: which benefit card is showing its long sentence.
 *
 * Keyed to input capability, never to viewport width. A pointer that can hover
 * needs nothing from this file — the CSS owns that route inside
 * `@media (hover: hover)`. A touch screen has no hover, so the card is chosen
 * by SCROLL position (George's call): one IntersectionObserver watches a band
 * around the middle of the scrollport and the card sitting in it is the active
 * one. Exactly one card is ever active, and when none is in the band the row
 * goes back to its short labels.
 *
 * Scroll container: Horizon's squeeze layout makes `.page-wrapper` the page
 * scroller at >=990px and `document.scrollingElement` below it. A touch device
 * can sit either side of that line, so both the observer root and the centre
 * line the tie-break measures against are read through
 * @theme/scroll-container — the same helper lf-timeline-row.js uses — and
 * rebuilt when the viewport crosses the breakpoint. With the document as root
 * the outward/inward rootMargin below is measured against the viewport; with
 * `.page-wrapper` it is measured against that element's box, which is the
 * scrollport on desktop.
 *
 * Nothing here knows about colour or opacity: the active card gets one
 * attribute and the CSS does the rest. Without JS the cards simply stay at rest
 * on touch, which is the same thing they do when nothing is in the band.
 */

import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

const TOUCH_QUERY = '(hover: none)';

/**
 * The band: the middle 30% of the scrollport. A card counts the instant its box
 * touches it (threshold 0) rather than at some share of its own height, so tall
 * and short cards hand over at the same place on screen.
 */
const BAND_MARGIN = '-35% 0px -35% 0px';

class LfPastureBenefits extends HTMLElement {
  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('[data-lf-pfl-card]'));
    if (this.cards.length === 0) return;

    this.inBand = new Set();
    this.touch = window.matchMedia(TOUCH_QUERY);
    this.refresh = this.refresh.bind(this);
    this.onIntersect = this.onIntersect.bind(this);

    this.touch.addEventListener('change', this.refresh);
    scrollContainerMediaQuery.addEventListener('change', this.refresh);

    this.refresh();
  }

  disconnectedCallback() {
    this.stop();
    this.touch?.removeEventListener('change', this.refresh);
    scrollContainerMediaQuery.removeEventListener('change', this.refresh);
  }

  /** Re-read the input mode and rebuild the observer under it. */
  refresh() {
    this.stop();
    if (!this.touch.matches) return;

    this.observer = new IntersectionObserver(this.onIntersect, {
      root: getIntersectionRoot(),
      rootMargin: BAND_MARGIN,
      threshold: 0,
    });

    this.cards.forEach((card) => this.observer.observe(card));
  }

  stop() {
    this.observer?.disconnect();
    this.observer = null;
    this.inBand?.clear();
    this.setActive(null);
  }

  onIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) this.inBand.add(entry.target);
      else this.inBand.delete(entry.target);
    });

    this.setActive(this.nearest());
  }

  /**
   * Several cards can share the band on a short screen, so the one whose own
   * centre is closest to the scrollport's centre wins. The rects are measured
   * here rather than taken off the entries: a card that entered the band on an
   * earlier callback carries a stale rect, and there are never more than a
   * handful of cards to measure.
   */
  nearest() {
    if (this.inBand.size === 0) return null;

    const middle = this.centreLine();
    let winner = null;
    let best = Infinity;

    this.inBand.forEach((card) => {
      const rect = card.getBoundingClientRect();
      const distance = Math.abs(rect.top + rect.height / 2 - middle);

      if (distance < best) {
        best = distance;
        winner = card;
      }
    });

    return winner;
  }

  /** The middle of the scrollport, in viewport coordinates. */
  centreLine() {
    const root = getIntersectionRoot();
    if (!root) return window.innerHeight / 2;

    return root.getBoundingClientRect().top + root.clientHeight / 2;
  }

  setActive(card) {
    if (card === this.active) return;
    this.active = card;
    this.cards.forEach((item) => item.toggleAttribute('data-lf-active', item === card));
  }
}

if (!customElements.get('lf-pasture-benefits')) {
  customElements.define('lf-pasture-benefits', LfPastureBenefits);
}
